import React, { createContext, useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import RouteConstants from "../../../constants/routeConstants.json";
import ContentData from "../../../constants/contentFile.json";
import { useCookies } from "react-cookie";
import { getTenantListApi, commonPlanList } from "../../../api/Api";
import ViewIcon from "../../../assets/images/right-more.png";
export const TenantListContext = createContext({});

export const TenantListContextProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
   const [searchParams] = useSearchParams();
   const pageFromUrl = searchParams.get("page");
  const [searchData, setSearchData] = useState("");
  const [tenantApiData, setTenantApiData] = useState([]);
  const [totalPages, setTotalPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(pageFromUrl ? Number(pageFromUrl) : 1);

  const [subscriptionFilter, setSubscriptionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [subscriptionPlanList, setSubscriptionPlanList] = useState("");
  const [tenantList, setTenantList] = useState([]);

  const [spinnerStatus, setSpinnerStatus] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isStatusExpanded, setIsStatusExpanded] = useState(false);
  const [isSubscriptionExpanded, setIsSubscriptionExpanded] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState("");
  const [selectedStatusId, setSelectedStatusId] = useState("");

  const [errorPopup, setErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState(false);
  const [statusValue, setStatusValue] = useState();
  const [buttonText, setButtonText] = useState("");
  const [cookies, setCookie, removeCookie] = useCookies(["user"]);

  // Tenant Permissions
  const permissionAccess = JSON.parse(
    localStorage.getItem("permissions")
  )?.filter((item) => {
    return item.feature === "67371df9534964294f80cfe4";
  })[0];
  const roleIdentifier = JSON.parse(localStorage.getItem("roleIdentifier"));
  const isSuperAdmin = roleIdentifier === "superadmin";

  useEffect(() => {
    getTenantList(searchData, subscriptionFilter, statusFilter, currentPage);
  }, [searchData, subscriptionFilter, statusFilter, currentPage]);

  const handleClearFilters = () => {
    setCurrentPage(1);
    setStatusFilter("");
    setSubscriptionFilter("");
    setShowDropdown(false);
    setSelectedSubscriptionId("");
    setSelectedStatusId("");
    setIsStatusExpanded(false);
    setIsSubscriptionExpanded(false);
  };

  const onClickFilterButton = (newSubscriptionId, newStatusId) => {
    setCurrentPage(1);
    setSubscriptionFilter(newSubscriptionId);
    setStatusFilter(newStatusId);
    setShowDropdown(false);
  };

  const handleToggle = (isOpen) => {
    setShowDropdown(isOpen);
  };

  useEffect(() => {
    setTenantList(
      tenantApiData?.map((tenant) => {
        const subscriptionPlanData = tenant?.subscriptionPlan;
        const formattedSubscriptionPlan = subscriptionPlanData
          ? `${subscriptionPlanData.name} (${subscriptionPlanData.billingCycle})`
          : "N/A"; // Default value if subscriptionPlan data is missing

        return {
          name: tenant?.organizationName,
          email: tenant?.organizationEmail,
          subscriptionPlan: formattedSubscriptionPlan,
          creationDate: formatDateTime(tenant?.createdAt),
          status: (
            <span
              style={{
                color:
                  tenant?.status === "A"
                    ? "#13BC5B" // Green for Active
                    : tenant?.status === "I"
                    ? "#EB5757" // Red for Inactive
                    : "#6c757d", // Default color (gray)
              }}
            >
              {tenant?.status === "A"
                ? "ACTIVE"
                : tenant?.status === "I"
                ? "INACTIVE"
                : "UNKNOWN"}
            </span>
          ),
          action: (
            <div onClick={() => viewTenantDetail(tenant?._id)}>
              <span>
                <img className="viewClickAdmin" src={ViewIcon} alt="view" />
              </span>
            </div>
          ),
          location: tenant?.location,
          id: tenant?._id,
        };
      })
    );
  }, [tenantApiData]);

  useEffect(() => {
    getSubscriptionPlans();
  }, []);

  const viewTenantDetail = (id) => {
    navigate(RouteConstants.VIEW_TENANT, { state: { id } });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A"; // Handle null/undefined dates
    const date = new Date(dateString);

    // Format date: dd-mm-yyyy
    const formattedDate = date
      .toLocaleDateString("en-GB") // British format: dd/mm/yyyy
      .replace(/\//g, "-"); // Replace slashes with dashes

    // Format time: hh:mm am/pm
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const formattedMinutes = String(minutes).padStart(2, "0"); // Ensure two-digit minutes
    const formattedTime = `${hours % 12 || 12} : ${formattedMinutes} ${
      hours >= 12 ? "pm" : "am"
    }`;

    return `${formattedDate}, ${formattedTime}`;
  };

  const onPageChange = (e) => {
    setCurrentPage(e);
    const params = new URLSearchParams(location.search)
    params.set("page", e);
    navigate(`${location?.pathname}?${params.toString()}`, {replace : true})
  };

  const onChangeSearch = (e) => {
    const specialCharacters = /[!@#$%^&*(),.?":{}|<>+\-_\/=;]/;
    if (specialCharacters.test(e.target.value)) {
      console.log("Special characters detected:", e.target.value);
    }

    // Update the state regardless
    setSearchData(e.target.value);
    setCurrentPage(1);
  };

  const onClickAddAdmin = () => {
    navigate(RouteConstants.ADD_TENANT);
  };

  const goToTenantActivity = () => {
    navigate(RouteConstants.ACTIVITY_TENANT);
  };
  const goToLogin = () => {
    navigate(RouteConstants.LOGIN);
    removeCookie("x-access-token");
  };

  const getTenantList = async (
    searchData,
    subscriptionFilter,
    statusFilter,
    page
  ) => {
    try {
      setSpinnerStatus(true);

      const tenantData = await getTenantListApi(
        searchData,
        subscriptionFilter,
        statusFilter,
        page
      );
      // console.log("tenantData", tenantData);

      if (tenantData?.status === 200) {
        setSpinnerStatus(false);
        setTenantApiData(tenantData?.data?.data?.tenants);
        setTotalPages(tenantData?.data?.data?.totalPages);
      } else {
        console.error("Unexpected response structure:", tenantData);
        setTenantApiData([]);
        setTotalPages(0);
      }
    } catch (error) {
      const responseStatus = error?.response?.data?.statusCode;
      const errorMessage =
        error?.response?.data?.message || ContentData.tenantPage.wentWrong;

      setSpinnerStatus(false);
      setStatusValue(responseStatus);
      setErrorPopupMessage(errorMessage);
      setErrorPopup(true);

      if (responseStatus === 400) {
        setButtonText(ContentData.tenantPage.tryAgain);
      } else if (responseStatus === 401 || responseStatus === 403) {
        setButtonText(ContentData.tenantPage.login);
      } else {
        setButtonText(ContentData.tenantPage.tryAgain);
      }
      setTenantApiData([]);
      setTotalPages(0);
    }
    setSpinnerStatus(false);
  };

  const getSubscriptionPlans = async () => {
    try {
      setSpinnerStatus(true);
      const listApiData = await commonPlanList();
      setSubscriptionPlanList(listApiData.data.data.subscriptionPlans);
    } catch (error) {
      const responseStatus = error?.response?.data?.statusCode;
      const errorMessage =
        error?.response?.data?.message || ContentData.tenantPage.wentWrong;

      setSpinnerStatus(false);
      setStatusValue(responseStatus);
      setErrorPopupMessage(errorMessage);
      setErrorPopup(true);

      if (responseStatus === 400) {
        setButtonText(ContentData.tenantPage.tryAgain);
      } else if (responseStatus === 401 || responseStatus === 403) {
        setButtonText(ContentData.tenantPage.login);
      } else {
        setButtonText(ContentData.tenantPage.tryAgain);
      }
    }
    setSpinnerStatus(false);
  };

  const providerValue = {
    onClickAddAdmin,
    tenantList,
    totalPages,
    onPageChange,
    currentPage,
    spinnerStatus,
    searchData,
    onChangeSearch,
    // setStatusFilter,
    // setSubscriptionFilter,
    subscriptionPlanList,
    showDropdown,
    // setShowDropdown,
    isStatusExpanded,
    setIsStatusExpanded,
    isSubscriptionExpanded,
    setIsSubscriptionExpanded,
    selectedSubscriptionId,
    setSelectedSubscriptionId,
    selectedStatusId,
    setSelectedStatusId,
    handleClearFilters,
    onClickFilterButton,
    handleToggle,
    setCurrentPage,
    goToTenantActivity,
    permissionAccess,
    getTenantList,
    errorPopup,
    setErrorPopup,
    errorPopupMessage,
    setErrorPopupMessage,
    buttonText,
    setButtonText,
    goToLogin,
    statusValue,
    setStatusValue,
    isSuperAdmin,
  };
  return (
    <TenantListContext.Provider value={providerValue}>
      {children}
    </TenantListContext.Provider>
  );
};
